import type { AttackDef, AttackEvent } from '../../types/combat';

export type AttackOverrides = Partial<Omit<AttackDef, 'events' | 'maskBones'>> & {
  events?: AttackEvent[];
  maskBones?: string[];
};

const cloneEvents = (events: AttackEvent[]): AttackEvent[] =>
  events.map((event) => ({ ...event, params: event.params ? { ...event.params } : undefined }));

const cloneMask = (mask?: string[]): string[] | undefined =>
  mask ? [...mask] : undefined;

const buildAttack = (base: AttackDef, overrides: AttackOverrides = {}): AttackDef => ({
  ...base,
  ...overrides,
  events: overrides.events ? cloneEvents(overrides.events) : cloneEvents(base.events),
  maskBones: overrides.maskBones ? cloneMask(overrides.maskBones) : cloneMask(base.maskBones)
});

const MELEE_HORIZONTAL_BASE: AttackDef = {
  id: 'melee_horizontal',
  animation: 'attack_horizontal',
  damage: 15,
  type: 'oneshot',
  cooldown: 0.1, // 100ms cooldown
  events: [
    { time: 0.0, type: 'attachWeapon', params: { bone: 'mixamorig:RightHand' } },
    { time: 0.25, type: 'enableHitbox' },
    { time: 0.50, type: 'disableHitbox' }
  ]
};

const MELEE_VERTICAL_BASE: AttackDef = {
  id: 'melee_vertical',
  animation: 'attack_vertical',
  damage: 20,
  type: 'oneshot',
  cooldown: 0.1, // 100ms cooldown
  events: [
    { time: 0.05, type: 'attachWeapon', params: { bone: 'mixamorig:RightHand' } },
    { time: 0.32, type: 'enableHitbox' },
    { time: 0.68, type: 'disableHitbox' }
  ]
};

const RANGED_PROJECTILE_BASE: AttackDef = {
  id: 'projectile_throw',
  animation: 'attack_horizontal',
  damage: 20,
  type: 'oneshot',
  events: [
    { time: 0.4, type: 'detachProjectile' }
  ]
};

export const createMeleeHorizontalAttack = (overrides?: AttackOverrides): AttackDef =>
  buildAttack(MELEE_HORIZONTAL_BASE, overrides);

export const createMeleeVerticalAttack = (overrides?: AttackOverrides): AttackDef =>
  buildAttack(MELEE_VERTICAL_BASE, overrides);

export const createRangedProjectileAttack = (overrides?: AttackOverrides): AttackDef =>
  buildAttack(RANGED_PROJECTILE_BASE, overrides);

const CHANNELED_BASE: AttackDef = {
  id: 'channeled_attack',
  animation: 'attack_horizontal',
  damage: 5,
  type: 'channeled',
  channeled: {
    loopAnimation: 'attack_horizontal',
    tickInterval: 0.1,
    lockFacing: false,
  },
  events: [],
};

export const createChanneledAttack = (overrides?: AttackOverrides): AttackDef =>
  buildAttack(CHANNELED_BASE, overrides);